/**
 * InlineReasoningCard Component
 *
 * Displays reasoning progress inline within chat messages. Once reasoning steps
 * stream in they are the live indicator (active step shimmers, each step shows
 * its stage icon + content); before the first step arrives it shows a single
 * shimmering placeholder status line.
 *
 * Design based on Figma: node-id=2644-3010
 */

import React from 'react';

import type { ReasoningStepData } from './ReasoningStepsList';
import { ReasoningStepsList } from './ReasoningStepsList';
import { ShimmerText } from './ShimmerText';

interface InlineReasoningCardProps {
  /** Status text displayed in the header (e.g., "Reading through Tech design requirements...") */
  statusText: string;
  /** Whether the chain is actively processing (shows spinner) */
  isProcessing: boolean;
  /** Current reasoning steps */
  steps: ReasoningStepData[];
  /** Unique identifier for this chain (used for state reset) */
  chainId?: string;
  /** Callback when a dynamic view is clicked */
  onOpenDynamicViewSidebar?: (viewId: number) => void;
  /** Organization ID for clickable entity badges in reasoning steps */
  orgId?: number;
}

/** Generic backend placeholder (e.g. "Processing step 2..." or a bare
 *  "Processing...") we replace with a friendlier label so the word
 *  "Processing" never reaches the UI. */
const GENERIC_STATUS_RE = /^\s*processing(\s+step)?\s*\d*\s*\.*\s*$/i;

/**
 * Resolve the placeholder status line text (only shown before any reasoning
 * step has streamed in). Meaningful backend descriptions (e.g. "Searching for
 * X") are kept as-is; the generic "Processing..." placeholder becomes
 * "Thinking...".
 */
function getFriendlyStatus(statusText: string): string {
  const trimmed = statusText.trim();
  if (trimmed && !GENERIC_STATUS_RE.test(trimmed)) {
    return statusText;
  }
  return 'Thinking…';
}

export const InlineReasoningCard: React.FC<InlineReasoningCardProps> = ({
  statusText,
  isProcessing,
  steps,
  chainId,
  onOpenDynamicViewSidebar,
  orgId,
}) => {
  const hasSteps = steps.some(s => !s.is_final);

  // Once steps stream in, they ARE the live indicator: the active step's label
  // shimmers and every step shows its own stage icon + content. Rendering a
  // separate status headline on top of that just duplicates the active step
  // (e.g. a "Thinking..." headline above a "Thinking" step), so we show the
  // headline ONLY as a placeholder before the first step arrives.
  if (hasSteps) {
    return (
      <div className='w-full'>
        {/* Reasoning steps flow inline in the message column — no card, no
            inner scrollbar. Height stays bounded because completed steps
            collapse to a single line in compact mode. The chat's own
            scroll-to-bottom follows new steps as they stream in. */}
        <ReasoningStepsList
          steps={steps}
          listId={chainId}
          onOpenDynamicViewSidebar={onOpenDynamicViewSidebar}
          orgId={orgId}
          compact
        />
      </div>
    );
  }

  // Placeholder: a single shimmering status line shown only until the first
  // reasoning step streams in. No leading icon — the ThinkingIndicator's brand
  // loader ring is the liveness cue.
  const displayStatus = getFriendlyStatus(statusText);

  return (
    <div className='w-full'>
      <div className='flex items-center py-1'>
        {/* The label itself shimmers as the single liveness cue (no spinner). */}
        {isProcessing ? (
          <ShimmerText className='flex-1 text-sm font-medium truncate'>
            {displayStatus}
          </ShimmerText>
        ) : (
          <p className='flex-1 text-sm font-medium text-fg-secondary truncate'>
            {displayStatus}
          </p>
        )}
      </div>
    </div>
  );
};

export default InlineReasoningCard;
